/**
 * Pure state for the flashcard review session (ADR 0037): front → flip → rate → next card.
 * No React and no network, so the flip-and-rate flow is unit-tested.
 */

export interface CardFace {
  id: string;
  deckTitle: string;
  front: string;
  back: string;
  isNew: boolean;
}

export type Rating = 1 | 2 | 3 | 4;

export const RATING_BUTTONS: { rating: Rating; label: string; hint: string }[] = [
  { rating: 1, label: 'Again', hint: 'in 10 min' },
  { rating: 2, label: 'Hard', hint: 'a bit later' },
  { rating: 3, label: 'Good', hint: 'later' },
  { rating: 4, label: 'Easy', hint: 'much later' },
];

export type Session =
  | { kind: 'loading'; done?: number }
  | { kind: 'front'; card: CardFace; done: number }
  | { kind: 'back'; card: CardFace; done: number }
  | { kind: 'saving'; card: CardFace; done: number; rating: Rating; requestKey: string }
  | { kind: 'finished'; done: number }
  | { kind: 'error'; message: string; done: number };

type SessionEvent =
  | { type: 'loaded'; card: CardFace | null }
  | { type: 'flip' }
  | { type: 'rate'; rating: Rating; requestKey: string }
  | { type: 'saved' }
  | { type: 'failed'; message: string };

const doneOf = (s: Session): number => s.done ?? 0;

export function reduce(s: Session, e: SessionEvent): Session {
  switch (e.type) {
    case 'loaded':
      return e.card
        ? { kind: 'front', card: e.card, done: doneOf(s) }
        : { kind: 'finished', done: doneOf(s) };
    case 'flip':
      return s.kind === 'front' ? { kind: 'back', card: s.card, done: s.done } : s;
    case 'rate':
      // A rating is only possible once the answer is showing, and only once per card.
      return s.kind === 'back'
        ? { kind: 'saving', card: s.card, done: s.done, rating: e.rating, requestKey: e.requestKey }
        : s;
    case 'saved':
      return s.kind === 'saving' ? { kind: 'loading', done: s.done + 1 } : s;
    case 'failed':
      return { kind: 'error', message: e.message, done: doneOf(s) };
  }
}

/** One key per tap, so a network retry of the same tap is applied once by the server. */
export function newRequestKey(rand: () => string = () => crypto.randomUUID()): string {
  return `card-${rand()}`;
}

/** Friendly text for a cards API error code. */
export function cardsError(code: string): string {
  switch (code) {
    case 'cards.daily_cap':
      return 'That is enough cards for today. Come back tomorrow.';
    case 'cards.new_cap':
      return 'No more new cards today. Come back tomorrow.';
    case 'cards.not_found':
      return 'This card is no longer available.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** Whether the launch link opens the Cards screen (`/cards` button in the bot). */
export function opensCards(startParam: string | null, search: string): boolean {
  if (startParam === 'cards') return true;
  const q = new URLSearchParams(search);
  return [q.get('screen'), q.get('startapp'), q.get('tgWebAppStartParam')].includes('cards');
}
